import { useQuery } from "@apollo/client/react";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router";

import { constantsData, selectionsData } from "@/data";
import { getReleasesListQuery } from "@/graph";
import { openAlert, setSelection } from "@/redux/actions";
import { displayErrorHandler } from "@/utils";

const useReleasesList = (
    isPaginated = true,
    hasAutoCompleteMenu = false,
    isCompleted = false,
    filters = {},
    importerData,
    artistId,
    searchValue,
) => {
    const [fetched, setFetched] = useState(false);

    const [params] = useSearchParams();

    const pageParam = parseInt(params.get("page")) || 1;

    const limitParam = parseInt(params.get("limit")) || 25;

    const [page, setPage] = useState(pageParam);

    const [limit, setLimit] = useState(limitParam);

    const dispatchedDataRef = useRef(null);

    const { t } = useTranslation();

    const dispatch = useDispatch();

    const selections = useSelector((state) => state.selectionsReducer);

    const { isAuthenticated } = useAuth0();

    const { releases: releasesKey } = selectionsData.keys;

    const { data, error, loading, refetch } = useQuery(getReleasesListQuery, {
        skip:
            !isAuthenticated ||
            (importerData && !importerData?.importing) ||
            (importerData && importerData.codesList?.length === 0 && importerData.upcsList?.length === 0),
        variables: {
            ...(isPaginated && {
                limit,
                page: page - 1,
            }),
            orderBy: { release_date: "desc" }, //eslint-disable-line
            where: {
                ...(Object.keys(filters)?.length > 0 && { ...filters }),
                isCompleted: { equals: isCompleted },
                ...((importerData || artistId) && {
                    OR: [
                        {
                            ...(artistId && {
                                artist_id: { equals: artistId }, //eslint-disable-line
                            }),
                        },
                        {
                            ...(importerData && {
                                album_code: { in: importerData.codesList }, //eslint-disable-line
                            }),
                        },
                        {
                            ...(importerData && {
                                upc: { in: importerData.upcsList },
                            }),
                        },
                    ],
                }),
                ...(searchValue && {
                    album_name: { contains: searchValue },
                }),
            },
        },
    });

    if (data?.listReleases && dispatchedDataRef.current !== data) {
        dispatchedDataRef.current = data;
        if (isPaginated && !hasAutoCompleteMenu) setFetched(true);
        else {
            const fetchedList = data.listReleases.posts?.map(({ album_name, album_name_ar, id, upc }) => ({
                albumNameAr: album_name_ar,
                label: album_name,
                upc,
                value: id,
            }));

            dispatch(
                setSelection({
                    [releasesKey]:
                        selections?.[releasesKey]?.length > 0 && hasAutoCompleteMenu
                            ? selections?.[releasesKey].concat(fetchedList)
                            : fetchedList,
                }),
            );

            setFetched(true);
        }
    }

    useEffect(
        () => {
            if (error?.message === constantsData.failedToFetch && !loading) {
                if (importerData) {
                    dispatch(
                        importerData.setInvalidImporterRelease({
                            isNetworkFailed: true,
                            originalData: { release: importerData?.currentRelease },
                            release: {
                                data: importerData?.currentRelease,
                                message: t("messages.networkFailure"),
                            },
                        }),
                    );
                } else {
                    displayErrorHandler(t("messages.networkFailure"), dispatch, openAlert);
                }
            }
        },
        [error, data], // eslint-disable-line
    );

    return {
        count: data?.listReleases?.meta?.nodeCount,
        data: data?.listReleases?.posts,
        error,
        fetched,
        limit,
        loading,
        page: data?.listReleases?.meta?.currentPage,
        refetch,
        searchKey: "album_name",
        setLimit,
        setPage,
    };
};

export default useReleasesList;
